import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  SECTIONS, type SectionName, type SectionValues,
  flattenVocab, unflattenVocab, type VocabValues,
} from "@klubora/shared";

export interface AuditMeta {
  updated_at: string;
  updated_by: string | null;
  source: string;
}

export interface TenantSettingsState<S extends SectionName> {
  values: SectionValues<S>;
  audit: Record<string, AuditMeta>;
  loading: boolean;
  /** KLUB-264 — the last read failed. `values` then hold the section defaults
   *  (or the last good read), which a caller that writes must not trust. */
  error: string | null;
}

export interface TenantSettingsResult<S extends SectionName> extends TenantSettingsState<S> {
  /** Re-runs the read (a "Tentar de novo" after `error`). */
  reload: () => void;
}

interface Row {
  key: string;
  value: unknown;
  updated_at: string;
  updated_by: string | null;
  source: string;
}

function buildState<S extends SectionName>(section: S, rows: Row[]): {
  values: SectionValues<S>;
  audit: Record<string, AuditMeta>;
} {
  const prefix = `${section}.`;
  const overrides: Record<string, unknown> = {};
  const flatVocab: Record<string, string> = {};
  const audit: Record<string, AuditMeta> = {};

  for (const row of rows) {
    if (!row.key.startsWith(prefix)) continue;
    const leaf = row.key.slice(prefix.length);
    audit[leaf] = {
      updated_at: row.updated_at,
      updated_by: row.updated_by,
      source: row.source,
    };
    if (section === "vocabulary") {
      flatVocab[leaf] = row.value as string;
    } else {
      overrides[leaf] = row.value;
    }
  }

  const defaults = SECTIONS[section].defaults as Record<string, unknown>;
  // Vocabulary defaults are nested (member.singular, member.plural, ...). A
  // shallow spread would clobber sibling leaves whenever the DB only has a
  // partial override, so merge at the flat layer and unflatten back. Mirrors
  // mergeVocabValues() in the write-tenant-setting edge function.
  const values = section === "vocabulary"
    ? unflattenVocab({ ...flattenVocab(defaults as VocabValues), ...flatVocab })
    : { ...defaults, ...overrides };

  return { values: values as SectionValues<S>, audit };
}

export function useTenantSettings<S extends SectionName>(
  tenantId: string | null,
  section: S,
): TenantSettingsResult<S> {
  // Initial loading flag is `true` only when we actually have a tenant to
  // load against. With no tenantId there is nothing to load, so the hook
  // should be idle from the first render and not write state from inside
  // an effect (react-hooks/set-state-in-effect).
  const [state, setState] = useState<TenantSettingsState<S>>(() => ({
    values: SECTIONS[section].defaults as SectionValues<S>,
    audit: {},
    loading: tenantId !== null,
    error: null,
  }));
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    let active = true;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const load = async () => {
      const { data, error } = await supabase
        .from("tenant_settings")
        .select("key, value, updated_at, updated_by, source")
        .eq("tenant_id", tenantId)
        .like("key", `${section}.%`);
      if (!active) return;
      if (error) {
        // Keep whatever was last read; only flag the failure.
        setState((prev) => ({ ...prev, loading: false, error: error.message || "Falha ao ler configurações." }));
        return;
      }
      const built = buildState(section, (data as Row[]) ?? []);
      setState({ ...built, loading: false, error: null });
    };

    void load();

    channel = supabase
      .channel(`platform_settings:${tenantId}:${section}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "tenant_settings",
          filter: `tenant_id=eq.${tenantId}` },
        () => void load(),
      )
      .subscribe();

    return () => {
      active = false;
      if (channel) supabase.removeChannel(channel);
    };
  }, [tenantId, section, attempt]);

  return { ...state, reload };
}
